import React, { useEffect, useState } from "react";
import { useEvent } from "../../contexts/EventContext";
import {
	downloadGalleryImages,
	getCategoryIds,
	getGalleryImages,
	getGalleryImagesPrivate,
	searchGalleryImage
} from "../../crud/gallery.crud";
import {
	CustomDrawer,
	CustomModal,
	GalleryPage,
	NotificationBox
} from "kh-design-system/components";
import { uploadImage } from "../../reusable/images";
import { UploadImage } from "../../crud/imageUpload";
import "react-toastify/dist/ReactToastify.css";
import "react-image-crop/dist/ReactCrop.css";
import { showToast, useScreenWidth } from "../../reusable/common";
import { useUser } from "../../contexts/AuthContext";
import { jwtToken } from "../../crud/axios";
import Authentication from "../Authentication/Authentication";
import { PUBLIC_FACE_SEARCH_EVENTS, galleryAccess } from "../../utils/constants";
import { getSubRoute } from "../../utils/core";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";

const Gallery: React.FC = () => {
	const isMobileScreen = useScreenWidth();
	const navigate = useNavigate();
	const { user, isRegistered } = useUser();
	const { eventDetails }: any = useEvent();
	const [openAuthModal, setOpenAuthModal] = useState(false);

	const [openFaceSearchPopup, setOpenFaceSearchPopup] = useState(false);
	const [openSuccessModal, setOpenDownloadPopup] = useState(false);

	const [isError, setIsError] = useState(false);
	const [toasterMessage, setToasterMessage] = useState("");
	const [notificationPopup, setNotificationPopup] = useState(false);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const onHandleFaceSearch = () => {
		setOpenAuthModal(true);
		localStorage.setItem("isFaceSearchClicked", "true");
	};

	const onHandleDownloadImages = () => {
		setOpenAuthModal(true);
		localStorage.setItem("isDownloadClicked", "true");
	};

	const isUserAuthenticated = PUBLIC_FACE_SEARCH_EVENTS?.includes(eventDetails?.event_id)
		? false
		: (!user && !jwtToken) || (!isRegistered && user && jwtToken);

	useEffect(() => {
		const path = localStorage.getItem("path");
		const actions = [
			{
				key: "FaceSearch",
				storageKey: "isFaceSearchClicked",
				setter: setOpenFaceSearchPopup
			},
			{
				key: "Download",
				storageKey: "isDownloadClicked",
				setter: setOpenDownloadPopup
			}
		];

		if (path) {
			navigate(`/${getSubRoute()}`);

			if (
				user &&
				isRegistered &&
				jwtToken &&
				eventDetails?.gallery_access !== galleryAccess.PRIVATE
			) {
				actions.forEach(({ storageKey, setter }) => {
					if (localStorage.getItem(storageKey)) {
						setter(true);
						localStorage.removeItem(storageKey);
					}
				});
			}

			if (isRegistered && user && jwtToken) {
				localStorage.removeItem("path");
			}
		}
	}, [localStorage.getItem("path"), user, isRegistered, jwtToken]);

	useEffect(() => {
		if (notificationPopup) {
			const timer = setTimeout(() => {
				setIsError(false);
				setNotificationPopup(false);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [notificationPopup]);

	useEffect(() => {
		if (user && isRegistered && jwtToken) {
			setOpenAuthModal(false);
			const actions = [
				{ key: "openFaceSearch", setter: setOpenFaceSearchPopup },
				{ key: "openDownloadPopup", setter: setOpenDownloadPopup }
			];
			actions.forEach(({ key, setter }) => {
				if (localStorage.getItem(key)) {
					setter(true);
					localStorage.removeItem(key);
				}
			});
		} else if ((!user && !jwtToken) || (!isRegistered && user && jwtToken)) {
			setEmail("");
			setPassword("");
		}
	}, [user, isRegistered, jwtToken]);

	useEffect(() => {
		if (openAuthModal) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
		};
	}, [openAuthModal]);

	return (
		<>
			{notificationPopup && <NotificationBox message={toasterMessage} isError={isError} />}
			<GalleryPage
				eventDetails={eventDetails}
				searchGalleryImage={searchGalleryImage}
				uploadImage={uploadImage}
				uploadImageFaceSearch={UploadImage}
				getCategoryIds={getCategoryIds}
				getGalleryImages={
					eventDetails?.gallery_access === galleryAccess.PRIVATE &&
					((!user && !jwtToken) || (!isRegistered && user && jwtToken))
						? ""
						: eventDetails?.gallery_access === galleryAccess.PRIVATE &&
							  isRegistered &&
							  user &&
							  jwtToken
							? getGalleryImagesPrivate
							: getGalleryImages
				}
				isStandAlonePage={true}
				showToast={showToast}
				openFaceSearchPopup={openFaceSearchPopup}
				setOpenFaceSearchPopup={setOpenFaceSearchPopup}
				setOpenDownloadPopup={setOpenDownloadPopup}
				openDownloadPopup={openSuccessModal}
				isUserAuthenticated={isUserAuthenticated}
				onHandleFaceSearch={onHandleFaceSearch}
				downloadGalleryImages={downloadGalleryImages}
				onHandleDownloadImages={onHandleDownloadImages}
				consentText="I hereby give my explicit consent for my photograph to be collected and processed for face-based search in the AI gallery."
				primaryColor={eventDetails?.gallery_brand_color}
				checkBoxWidth="54px"
				checkBoxHeight="25px"
				checkBoxMobileWidth="54px"
				checkBoxMobileHeight="25px"
				checkBoxTickIcon={<Icon icon="material-symbols:check" width="20" height="26" />}
			/>

			{openAuthModal && !isMobileScreen && (
				<CustomModal
					openCustomModal={openAuthModal}
					onClose={() => {
						setOpenAuthModal(false);
						localStorage.removeItem("isFaceSearchClicked");
						localStorage.removeItem("isDownloadClicked");
					}}
					maxWidth="450px"
					showModalAction={true}
					component={
						<>
							<Authentication
								setIsError={setIsError}
								isError={isError}
								setToasterMessage={setToasterMessage}
								toasterMessage={toasterMessage}
								setNotificationPopup={setNotificationPopup}
								notificationPopup={notificationPopup}
								email={email}
								password={password}
							/>
						</>
					}
				/>
			)}

			{openAuthModal && isMobileScreen && (
				<CustomDrawer
					open={openAuthModal}
					onClose={() => {
						setOpenAuthModal(false);
					}}
					onOpen={() => {
						setOpenAuthModal(true);
					}}
					maxHeight="100vh"
					minHeight="100vh"
					showModalAction={true}
					component={
						<>
							<Authentication
								setIsError={setIsError}
								isError={isError}
								setToasterMessage={setToasterMessage}
								toasterMessage={toasterMessage}
								setNotificationPopup={setNotificationPopup}
								notificationPopup={notificationPopup}
								email={email}
								password={password}
							/>
						</>
					}
				/>
			)}
			<style>{`
			.custom-modal-content .custom-checkbox{
				align-items: flex-start;
				margin-top: 14px!important;
			}
			.custom-modal-close{
				padding-bottom: 0px !important;
			}

			.checkbox-tick{
				font-size: 14px!important;
			}
			.face-search-buttons{
				width: 307px;
			}
			.checkbox-label{
				text-align:left;
				font-size: 14px;
			}
			.gallery-face-search-desc{
				margin-top: 8px!important;
			}
			.face-search-capture-error{
				padding-top: 10px;
			}
			.gallery-face-search-wrapper-mobile{
				padding-bottom: 15px;

			}
			`}</style>
		</>
	);
};

export default Gallery;
